import React, { useState, useEffect, useCallback } from 'react';
import authedFetch from '../../lib/authedFetch';
import secureStorage from '../../lib/secureStorage';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from '../ui/alert-dialog';
import { toast } from 'sonner';
import { Database, Loader2, CheckCircle2, AlertCircle, Users, Trash2, BarChart3 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SeedDemoTile() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [progress, setProgress] = useState(null); // {label, pct}

  const authHeaders = () => ({ Authorization: `Bearer ${secureStorage.getItem('session_token')}` });

  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authedFetch(`${API_URL}/api/admin/seed/demo-status`, { headers: authHeaders() });
      if (res.ok) setStatus(await res.json());
    } catch (e) { /* noop */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const runSeed = async () => {
    setConfirmOpen(false);
    setSeeding(true);
    setProgress({ label: 'Creating orgs, logins, Capability & Transfer Health data…', pct: 5 });
    try {
      // 1) Full three-org demo: orgs + logins + trend events + ROE Transfer Health
      //    (Confidence/Application/Behavior) + Capability(PE)/LEI/Maturity + Acme's rich set.
      const res = await authedFetch(`${API_URL}/api/admin/seed/seed-base`, { method: 'POST', headers: authHeaders() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Base seed failed');
      const orgsCreated = (data.base?.orgs || []).filter((o) => o.status === 'created').length;
      const usersCreated = (data.base?.users || []).filter((u) => u.status === 'created').length;
      const events = data.events?.seeded?.length || 0;
      toast.success(`Demo seeded — ${orgsCreated} org(s) + ${usersCreated} login(s), ${events} event(s), Capability & Transfer Health data for Acme, Seertech & Vantage`);
      if (data.acme_rich && data.acme_rich !== 'seeded') toast.warning(`Acme rich dataset: ${data.acme_rich}`);
      if (data.events?.skipped_orgs?.length) toast.warning(`Skipped events (org not found): ${data.events.skipped_orgs.join(', ')}`);

      // 2) Analytics dataset (~12.7k LEI/PE/KPI/ROE rows) — runs in the background
      setProgress({ label: 'Generating analytics data…', pct: 15 });
      const dres = await authedFetch(`${API_URL}/api/admin/seed/seed-lei-pe-kpi`, { method: 'POST', headers: authHeaders() });
      const ddata = await dres.json();
      if (!dres.ok) throw new Error(ddata.detail || 'Failed to start analytics seed');

      // 3) Poll progress until complete (or timeout ~2min)
      let done = false;
      for (let i = 0; i < 60 && !done; i++) {
        await new Promise((r) => setTimeout(r, 2000));
        const pres = await authedFetch(`${API_URL}/api/admin/seed/seed-lei-pe-kpi/progress`, { headers: authHeaders() });
        if (!pres.ok) continue;
        const p = await pres.json();
        if (p.status === 'failed') throw new Error(p.error || 'Analytics seed failed');
        const total = p.total || 0;
        const target = p.target || 12702;
        const pct = Math.min(99, 15 + Math.round((total / target) * 84));
        const phase = (p.phase || '').replace(/_/g, ' ');
        setProgress({ label: p.status === 'complete' ? 'Finalising…' : `Analytics: ${phase || 'working'} (${total.toLocaleString()})`, pct: p.status === 'complete' ? 100 : pct });
        if (p.status === 'complete') done = true;
      }
      if (done) toast.success('Environment fully lit up — analytics data seeded');
      else toast.warning('Analytics seed still running — it will finish shortly');
      await loadStatus();
    } catch (e) {
      toast.error(e.message || 'Seed failed');
    } finally { setSeeding(false); setProgress(null); }
  };

  const runReset = async () => {
    setResetOpen(false);
    setResetting(true);
    try {
      const res = await authedFetch(`${API_URL}/api/admin/seed/seed-reset`, { method: 'POST', headers: authHeaders() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Reset failed');
      const r = data.removed || {};
      toast.success(`Removed ${r.orgs?.length || 0} org(s), ${r.users?.length || 0} user(s), ${r.events || 0} event(s)`);
      await loadStatus();
    } catch (e) {
      toast.error(e.message || 'Reset failed');
    } finally { setResetting(false); }
  };

  const anyMissing = status?.orgs?.some((o) => !o.org_exists || !o.user_exists);
  const anySeeded = status?.orgs?.some((o) => o.seeded_events > 0);

  return (
    <Card data-testid="seed-demo-tile">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-bold flex items-center gap-2">
          <Database className="h-4 w-4 text-indigo-600" /> Demo Data
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-slate-500">One-click, fully lights up a fresh environment: demo orgs (Acme, Seertech &amp; Vantage), an org-admin login for each, Learning Health Trends events, ROE <strong>Transfer Health</strong> (Confidence / Application / Behavior), <strong>Capability Health</strong> (capability, retention, learning-effectiveness), plus the full analytics dataset. Idempotent &amp; safe to re-run.</p>
        <div className="space-y-2" data-testid="seed-demo-status">
          {loading && !status ? (
            <p className="text-xs text-slate-400 flex items-center gap-1.5"><Loader2 className="h-3 w-3 animate-spin" /> Checking status…</p>
          ) : status ? (<>
            {status.orgs.map((o) => (
            <div key={o.org_name} className="space-y-1">
              <div className="flex items-center justify-between text-xs">
                <span className="text-slate-600 font-medium">{o.org_name}</span>
                {!o.org_exists ? (
                  <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200"><AlertCircle className="h-3 w-3 mr-1" />Org missing</Badge>
                ) : o.seeded_events > 0 ? (
                  <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200" data-testid={`seed-status-${o.org_name.split(' ')[0].toLowerCase()}`}><CheckCircle2 className="h-3 w-3 mr-1" />{o.seeded_events} events</Badge>
                ) : (
                  <Badge variant="outline" className="text-[10px] bg-slate-50 text-slate-500 border-slate-200">No events</Badge>
                )}
              </div>
              <div className="flex items-center justify-between text-[11px] pl-1">
                <span className="text-slate-400 flex items-center gap-1"><Users className="h-3 w-3" />Admin login</span>
                {o.user_exists ? (
                  <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200" data-testid={`seed-user-${o.org_name.split(' ')[0].toLowerCase()}`}><CheckCircle2 className="h-3 w-3 mr-1" />Ready</Badge>
                ) : (
                  <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200"><AlertCircle className="h-3 w-3 mr-1" />Missing</Badge>
                )}
              </div>
            </div>
            ))}
            <div className="flex items-center justify-between text-xs pt-1 border-t border-slate-100" data-testid="seed-data-status">
              <span className="text-slate-600 font-medium flex items-center gap-1"><BarChart3 className="h-3 w-3 text-slate-400" />Analytics data</span>
              {status.data_seeded ? (
                <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200"><CheckCircle2 className="h-3 w-3 mr-1" />{(status.data_total || 0).toLocaleString()} rows</Badge>
              ) : status.data_status === 'in_progress' ? (
                <Badge variant="outline" className="text-[10px] bg-indigo-50 text-indigo-700 border-indigo-200"><Loader2 className="h-3 w-3 mr-1 animate-spin" />Generating…</Badge>
              ) : (
                <Badge variant="outline" className="text-[10px] bg-slate-50 text-slate-500 border-slate-200">Not seeded</Badge>
              )}
            </div>
          </>) : <p className="text-xs text-rose-500">Could not load status</p>}
        </div>

        {seeding && progress && (
          <div className="space-y-1" data-testid="seed-progress">
            <div className="h-1.5 w-full rounded-full bg-slate-100 overflow-hidden">
              <div className="h-full bg-indigo-600 transition-all duration-500" style={{ width: `${progress.pct}%` }} />
            </div>
            <p className="text-[11px] text-slate-500">{progress.label}</p>
          </div>
        )}

        <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
          <AlertDialogTrigger asChild>
            <Button size="sm" disabled={seeding} className="w-full" data-testid="seed-demo-btn">
              {seeding ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Seeding…</> : (anyMissing ? 'Create demo orgs & seed' : 'Seed Demo Data')}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent data-testid="seed-demo-confirm">
            <AlertDialogHeader>
              <AlertDialogTitle>Seed demo data?</AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="space-y-2 text-sm">
                  <p>This fully lights up the environment end-to-end:</p>
                  <ul className="list-disc pl-5 space-y-1 text-slate-600">
                    <li>Create <strong>Acme</strong>, <strong>Seertech</strong> &amp; <strong>Vantage</strong> orgs if missing, each with an <strong>org-admin login</strong> (existing accounts are never overwritten)</li>
                    <li>Seed the Learning Health Trends program-launch events</li>
                    <li>Seed <strong>ROE Transfer Health</strong> (Confidence / Application / Behavior + application evidence) and <strong>Capability Health</strong> (capability, retention, learning-effectiveness) for all three orgs, plus Acme's rich multi-cohort dataset</li>
                    <li>Generate the full <strong>analytics dataset</strong> (~12.7k LEI/PE/KPI/ROE rows across the demo orgs)</li>
                  </ul>
                  <p className="text-xs text-slate-500">Takes ~40–90s. Idempotent — no existing org, user, or password is clobbered. The seed replaces prior <em>seed</em> data for the demo orgs only.</p>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="seed-demo-cancel">Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={runSeed} data-testid="seed-demo-confirm-btn">Yes, seed now</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <AlertDialog open={resetOpen} onOpenChange={setResetOpen}>
          <AlertDialogTrigger asChild>
            <Button size="sm" variant="ghost" disabled={resetting || seeding} className="w-full text-rose-600 hover:text-rose-700 hover:bg-rose-50" data-testid="reset-demo-btn">
              {resetting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Resetting…</> : <><Trash2 className="h-4 w-4 mr-2" />Reset demo data</>}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent data-testid="reset-demo-confirm">
            <AlertDialogHeader>
              <AlertDialogTitle>Reset demo data?</AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="space-y-2 text-sm">
                  <p>This removes <strong>only what the demo seed created</strong>:</p>
                  <ul className="list-disc pl-5 space-y-1 text-slate-600">
                    <li>Seeded demo orgs (only ones this tool created — never a real tenant)</li>
                    <li>Seeded org-admin logins (existing accounts are never removed)</li>
                    <li>The Learning Health Trends demo events</li>
                  </ul>
                  <p className="text-xs text-rose-500">Real, imported data is left completely untouched.</p>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="reset-demo-cancel">Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={runReset} className="bg-rose-600 hover:bg-rose-700" data-testid="reset-demo-confirm-btn">Yes, reset</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  );
}
